import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function AdminCancelRequests() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [decision, setDecision] = useState("approve");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/cancel-requests"); setRows(data); };
  useEffect(() => { load(); }, []);

  const sb = (s) => {
    const map = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
    return <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{s}</Badge>;
  };

  const open = (row, d) => { setActive(row); setDecision(d); setNote(""); };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/cancel-requests/${active.id}/decision`, { decision, note });
      toast.success(`Request ${decision}d`);
      setActive(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Cancellation requests</h1>
      <p className="text-slate-500 mt-1">Approve or reject user-submitted cancellation requests.</p>

      <Card className="mt-6 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Booking</TableHead><TableHead>User</TableHead><TableHead>Amount</TableHead>
              <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Requested</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`cancel-req-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.booking_id.slice(0, 8)}</TableCell>
                <TableCell>{r.user_email}</TableCell>
                <TableCell className="font-semibold">${Number(r.amount).toFixed(2)} {r.currency}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-xs truncate">{r.reason}</TableCell>
                <TableCell>{sb(r.status)}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  {r.status === "pending" && (
                    <div className="flex gap-2 justify-end">
                      <Button data-testid={`cancel-req-approve-${r.id}`} size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => open(r, "approve")}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button data-testid={`cancel-req-reject-${r.id}`} size="sm" variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => open(r, "reject")}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                  {r.status !== "pending" && r.admin_note && (
                    <div className="text-xs text-slate-500 italic">"{r.admin_note}"</div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No cancellation requests</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="cancel-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">{decision === "approve" ? "Approve" : "Reject"} cancellation</DialogTitle>
            <DialogDescription>
              {decision === "approve"
                ? "This will mark the booking as cancelled and add a cancellation record."
                : "The booking will remain active. The customer will be notified that the request was declined."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Internal note {decision === "reject" ? "(shown to customer)" : "(optional)"}</Label>
            <Textarea data-testid="cancel-decision-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActive(null)}>Cancel</Button>
            <Button data-testid="cancel-decision-confirm" disabled={busy} onClick={submit}
                    className={decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
              {busy ? "Submitting..." : `Confirm ${decision}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
